import { getFloorplanAreaAppearance } from '../../../components/floorplan/areaAppearance';
import { printTeamColors } from '../../../components/floorplan/teamColors';
import { deskNameLines } from '../../../components/floorplan/utils';
import { SBG_HEX } from '../../../theme/sbgTokens';
import { FloorplanAssignment, FloorplanLayoutDetail } from '../../../types/floorplan';
import { formatDateTime, formatNumber, formatPercent } from '../../../utils/format';

/**
 * The printable floor plan. It renders in its own window with no app styles,
 * so — like email HTML — it uses the literal light-theme `SBG_HEX` values
 * rather than CSS custom properties.
 */

export const sortDeskNumbers = (left: string, right: string) => {
  const leftNumber = Number(left);
  const rightNumber = Number(right);

  if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return left.localeCompare(right);
};

const PRINT_BODY_FONT = '"Instrument Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const PRINT_DISPLAY_FONT = '"Host Grotesk", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';

const escapePrintHtml = (value: string | number | null | undefined) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const printDeskName = (name: string) => {
  const [first, last] = deskNameLines(name);
  return last ? `${escapePrintHtml(first)}<br>${escapePrintHtml(last)}` : escapePrintHtml(first);
};

const desksLabel = (assigned: number, total: number) =>
  `${formatNumber(assigned)} of ${formatNumber(total)} ${total === 1 ? 'desk' : 'desks'} assigned`;

export const buildPrintHtml = (layoutDetail: FloorplanLayoutDetail, assignments: FloorplanAssignment[]) => {
  const assignmentByDeskId = new Map(assignments.map((assignment) => [assignment.deskId, assignment]));
  const totalDesks = layoutDetail.layout.levels.reduce((sum, level) => sum + level.desks.length, 0);
  const assignedDesks = assignments.length;
  const occupancy = totalDesks > 0 ? (assignedDesks / totalDesks) * 100 : null;
  const printedAt = formatDateTime(new Date());
  // Every team seated on the plan, in its color, for the legend.
  const teams = new Map<string, string>();
  assignments.forEach((assignment) => {
    const { teamName, teamColor } = assignment.employee;
    if (teamName && !teams.has(teamName)) teams.set(teamName, teamColor);
  });
  const teamLegend = Array.from(teams.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([name, color]) => {
      const tone = printTeamColors(color);
      const swatch = tone
        ? `background:${tone.fill};border:1px solid ${tone.edge};box-shadow:inset 0 2px 0 0 ${tone.solid};`
        : `background:${SBG_HEX.white};border:1px solid ${SBG_HEX.slate400};`;
      return `<span class="legend__item"><span class="swatch" style="${swatch}"></span>${escapePrintHtml(name)}</span>`;
    })
    .join('');

  const levelMarkup = layoutDetail.layout.levels
    .map((level) => {
      const scale = Math.min(980 / level.width, 1);
      const levelAssignedCount = level.desks.filter((desk) => assignmentByDeskId.has(desk.id)).length;
      const desks = [...level.desks]
        .sort((left, right) => sortDeskNumbers(left.number, right.number))
        .map((desk) => {
          const assignment = assignmentByDeskId.get(desk.id);
          const label = assignment?.employee.name || '';
          // Team colors are validated hex (printTeamColors), safe to inline.
          const team = assignment ? printTeamColors(assignment.employee.teamColor) : null;
          const teamStyle = team
            ? `background:${team.fill};border:1px solid ${team.edge};box-shadow:inset 0 2px 0 0 ${team.solid};`
            : '';

          return `
            <div class="desk ${assignment ? 'desk--assigned' : 'desk--open'}" style="
              ${teamStyle}
              left:${desk.x * scale}px;
              top:${desk.y * scale}px;
              width:${desk.width * scale}px;
              height:${desk.height * scale}px;
              font-size:${Math.max(8 * scale, 5.75)}px;
            ">
              <div class="desk__number">${escapePrintHtml(desk.number)}</div>
              ${assignment ? `<div class="desk__name" title="${escapePrintHtml(label)}">${printDeskName(label)}</div>` : ''}
            </div>
          `;
        })
        .join('');

      const areas = level.areas
        .map((area) => {
          const appearance = getFloorplanAreaAppearance(area.type, { medium: 'print' });
          return `
            <div class="area" style="
              left:${area.x * scale}px;
              top:${area.y * scale}px;
              width:${area.width * scale}px;
              height:${area.height * scale}px;
              font-size:${Math.max(9 * scale, 6)}px;
              background:${appearance.background};
              border:${appearance.border};
              border-radius:${appearance.borderRadius}px;
              color:${appearance.labelColor};
              align-items:${appearance.labelAtTop ? 'flex-start' : 'center'};
              justify-content:${appearance.labelAtTop ? 'flex-start' : 'center'};
              text-align:${appearance.labelAtTop ? 'left' : 'center'};
              padding:${appearance.hideLabel ? 0 : 4}px;
            ">${appearance.hideLabel ? '' : escapePrintHtml(area.label)}</div>
          `;
        })
        .join('');

      return `
        <section class="level">
          <div class="level__header">
            <div>
              <div class="overline">Floor</div>
              <h2>${escapePrintHtml(level.name)}</h2>
            </div>
            <div class="level__meta">${desksLabel(levelAssignedCount, level.desks.length)}</div>
          </div>
          <div class="canvas" style="width:${level.width * scale}px;height:${level.height * scale}px;">
            ${areas}
            ${desks}
          </div>
        </section>
      `;
    })
    .join('');

  return `
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8">
        <title>${escapePrintHtml(layoutDetail.layout.name)} floor plan</title>
        <style>
          @page { size: landscape; margin: 0.35in; }
          * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { margin: 0; background: ${SBG_HEX.white}; color: ${SBG_HEX.navy}; font-family: ${PRINT_BODY_FONT}; font-size: 12px; }
          .page { padding: 18px 20px 20px; }
          .cover {
            display: flex; align-items: flex-end; justify-content: space-between; gap: 24px;
            padding-bottom: 14px; margin-bottom: 16px; border-bottom: 1px solid ${SBG_HEX.slate100};
          }
          .overline { color: ${SBG_HEX.slate600}; font-size: 9px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; }
          h1, h2 { font-family: ${PRINT_DISPLAY_FONT}; font-weight: 500; color: ${SBG_HEX.navy}; margin: 0; }
          h1 { font-size: 24px; line-height: 1.15; letter-spacing: -0.01em; margin-top: 4px; }
          h2 { font-size: 15px; line-height: 1.2; margin-top: 2px; }
          .meta { margin-top: 6px; color: ${SBG_HEX.slate600}; font-size: 10px; font-variant-numeric: tabular-nums; }
          .summary { display: grid; grid-template-columns: repeat(3, minmax(88px, 1fr)); column-gap: 24px; }
          .summary__item { border-top: 1px solid ${SBG_HEX.slate200}; padding-top: 8px; }
          .summary__label { color: ${SBG_HEX.blue600}; font-size: 8px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; }
          .summary__value { margin-top: 4px; font-family: ${PRINT_DISPLAY_FONT}; font-weight: 500; font-size: 18px; line-height: 1; font-variant-numeric: tabular-nums; }
          .legend { display: flex; flex-wrap: wrap; gap: 6px 16px; align-items: center; margin: 0 0 14px; color: ${SBG_HEX.slate600}; font-size: 10px; }
          .legend__label { color: ${SBG_HEX.navy}; font-weight: 500; }
          .legend__item { display: inline-flex; align-items: center; gap: 6px; }
          .swatch { width: 16px; height: 11px; border-radius: 3px; }
          .swatch--assigned { background: ${SBG_HEX.white}; border: 1px solid ${SBG_HEX.slate400}; }
          .swatch--open { background: ${SBG_HEX.slate25}; border: 1px dashed ${SBG_HEX.slate200}; }
          .swatch--room { background: ${SBG_HEX.white}; border: 2px solid ${SBG_HEX.slate400}; }
          .level { margin-bottom: 20px; break-inside: avoid; page-break-inside: avoid; }
          .level__header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 8px; }
          .level__meta { color: ${SBG_HEX.slate600}; font-size: 10px; font-variant-numeric: tabular-nums; }
          .canvas {
            position: relative; overflow: hidden; border-radius: 8px;
            border: 1px solid ${SBG_HEX.slate200}; background-color: ${SBG_HEX.white};
            background-image:
              linear-gradient(to right, ${SBG_HEX.slate100} 1px, transparent 1px),
              linear-gradient(to bottom, ${SBG_HEX.slate100} 1px, transparent 1px);
            background-size: 30px 30px;
          }
          .area { position: absolute; display: flex; overflow: hidden; font-weight: 500; line-height: 1.2; }
          .desk {
            position: absolute; overflow: hidden; border-radius: 6px; padding: 3px 4px;
            display: flex; flex-direction: column; gap: 2px;
          }
          .desk--assigned { background: ${SBG_HEX.white}; border: 1px solid ${SBG_HEX.slate400}; }
          .desk--open { background: ${SBG_HEX.slate25}; border: 1px dashed ${SBG_HEX.slate200}; }
          .desk__number { color: ${SBG_HEX.slate500}; font-weight: 500; line-height: 1; font-variant-numeric: tabular-nums; }
          .desk__name {
            color: ${SBG_HEX.navy}; font-weight: 600; line-height: 1.15;
            overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
          }
          .footer {
            margin-top: 14px; padding-top: 10px; border-top: 1px solid ${SBG_HEX.slate100};
            color: ${SBG_HEX.slate600}; font-size: 9px; display: flex; justify-content: space-between; gap: 16px;
          }
        </style>
      </head>
      <body>
        <main class="page">
          <header class="cover">
            <div>
              <div class="overline">SBG Funding · Floor plan</div>
              <h1>${escapePrintHtml(layoutDetail.layout.name)}</h1>
              <div class="meta">Printed ${escapePrintHtml(printedAt)}</div>
            </div>
            <div class="summary">
              <div class="summary__item">
                <div class="summary__label">Desks</div>
                <div class="summary__value">${formatNumber(totalDesks)}</div>
              </div>
              <div class="summary__item">
                <div class="summary__label">Assigned</div>
                <div class="summary__value">${formatNumber(assignedDesks)}</div>
              </div>
              <div class="summary__item">
                <div class="summary__label">Occupancy</div>
                <div class="summary__value">${formatPercent(occupancy)}</div>
              </div>
            </div>
          </header>
          ${teamLegend ? `<div class="legend"><span class="legend__label">Teams</span>${teamLegend}</div>` : ''}
          <div class="legend">
            <span class="legend__label">Desks</span>
            <span class="legend__item"><span class="swatch swatch--open"></span>Open desk</span>
            <span class="legend__item"><span class="swatch swatch--room"></span>Office or meeting room</span>
          </div>
          ${levelMarkup}
          <footer class="footer">
            <span>${escapePrintHtml(layoutDetail.layout.name)} · ${desksLabel(assignedDesks, totalDesks)}</span>
            <span>SBG Funding internal</span>
          </footer>
        </main>
      </body>
    </html>
  `;
};
